import { useState, useMemo } from 'react';
import { Loader2, Undo2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { PaymentProvider } from '@masters-club/types';

interface RefundDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paymentAmount: number;
  currency: string;
  provider: PaymentProvider;
  isPending: boolean;
  onConfirm: (refundAmount?: number) => void;
}

const PROVIDER_DESCRIPTION: Partial<Record<PaymentProvider, string>> = {
  [PaymentProvider.MULTICARD]: 'Запрос будет отправлен в Multicard/Rahmat. Статус обновится автоматически после ответа.',
  [PaymentProvider.CLOUDPAYMENTS]: 'Запрос будет отправлен в CloudPayments. Деньги вернутся на карту клиента автоматически.',
  [PaymentProvider.MANUAL]: 'Платёж будет помечен как возвращённый. Деньги нужно вернуть вручную.',
};

const SUPPORTS_PARTIAL: ReadonlySet<PaymentProvider> = new Set([
  PaymentProvider.CLOUDPAYMENTS,
  PaymentProvider.MULTICARD,
]);

function formatAmount(value: number, currency: string): string {
  const formatted = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
  return `${formatted} ${currency}`;
}

export function RefundDialog({
  open,
  onOpenChange,
  paymentAmount,
  currency,
  provider,
  isPending,
  onConfirm,
}: RefundDialogProps) {
  const canPartial = SUPPORTS_PARTIAL.has(provider);
  const [mode, setMode] = useState<'full' | 'partial'>('full');
  const [amountInput, setAmountInput] = useState<string>('');

  const parsedAmount = useMemo(() => {
    if (mode !== 'partial') { return null; }
    const normalized = amountInput.replace(',', '.').trim();
    if (normalized === '') { return null; }
    const num = Number(normalized);
    if (!Number.isFinite(num)) { return null; }
    return num;
  }, [mode, amountInput]);

  const isAmountValid = parsedAmount !== null && parsedAmount > 0 && parsedAmount < paymentAmount;
  const canSubmit = mode === 'full' || isAmountValid;

  const validationError = (() => {
    if (mode !== 'partial' || amountInput === '') { return null; }
    if (parsedAmount === null) { return 'Введите корректное число'; }
    if (parsedAmount <= 0) { return 'Сумма должна быть больше 0'; }
    if (parsedAmount >= paymentAmount) { return `Для возврата всей суммы выберите «Полный возврат»`; }
    return null;
  })();

  const handleSubmit = () => {
    if (!canSubmit) { return; }
    if (mode === 'full') {
      onConfirm();
    } else if (parsedAmount !== null) {
      onConfirm(parsedAmount);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Оформить возврат</DialogTitle>
          <DialogDescription>
            {PROVIDER_DESCRIPTION[provider] ?? 'Деньги будут возвращены клиенту. Это действие необратимо.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm">
            <span className="text-muted-foreground">Сумма платежа:</span>{' '}
            <span className="font-mono font-medium">{formatAmount(paymentAmount, currency)}</span>
          </div>

          <RadioGroup
            value={mode}
            onValueChange={(v) => setMode(v as 'full' | 'partial')}
            className="gap-3"
          >
            <Label
              htmlFor="refund-full"
              className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer font-normal"
            >
              <RadioGroupItem value="full" id="refund-full" className="mt-1" />
              <div className="flex-1">
                <span className="font-medium">Полный возврат</span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Вся сумма платежа. Заказ будет переведён в статус «Возврат», доступы клиента будут отозваны.
                </p>
              </div>
            </Label>

            <Label
              htmlFor="refund-partial"
              className={`flex items-start gap-3 rounded-lg border p-3 font-normal ${canPartial ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
            >
              <RadioGroupItem value="partial" id="refund-partial" className="mt-1" disabled={!canPartial} />
              <div className="flex-1">
                <span className="font-medium">Частичный возврат</span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {canPartial
                    ? 'Указанная сумма. Доступы клиента сохраняются, заказ остаётся активным.'
                    : 'Не поддерживается для этого провайдера.'}
                </p>
                {mode === 'partial' && canPartial && (
                  <div className="mt-3 space-y-1">
                    <Label htmlFor="refund-amount" className="text-xs">
                      Сумма возврата ({currency})
                    </Label>
                    <Input
                      id="refund-amount"
                      type="text"
                      inputMode="decimal"
                      placeholder={`Меньше ${formatAmount(paymentAmount, currency)}`}
                      value={amountInput}
                      onChange={(e) => setAmountInput(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="font-mono"
                    />
                    {validationError && (
                      <p className="text-xs text-destructive">{validationError}</p>
                    )}
                  </div>
                )}
              </div>
            </Label>
          </RadioGroup>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="cursor-pointer">
            Отмена
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90 cursor-pointer"
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Undo2 className="h-4 w-4 mr-2" />
            )}
            {mode === 'full' ? 'Вернуть полностью' : 'Вернуть частично'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
